import { parseEther, toHex } from 'viem';

import { zeroAddress, getContractValue, getContractJSONvalue } from './utils.js';
import { ipfsParts, ipfsFetch, getIpfsFolder, uploadIpfsFolder } from './ipfs.js';

import { walletAddress } from './stores.js';

export const setupVault = async (chain, data, oracle) => {
  const holders = [];
  const shares = [];
  for (const st of data.stakeholders) {
    holders.push(st.wallet);
    shares.push(st.shares);
  }
  const [vault, fee] = await Promise.all([
    getContractValue(chain, oracle, 'getKey', [ data.vault || 'defaultVault' ]),
    getContractValue(chain, oracle, 'getKey', [ data.fee || 'vaultDeployFee' ])
  ]);
  return { data: [ data.name, holders, shares, vault, parseEther(fee) ], fee: parseEther(fee) }
}

const setupResources = async (name, data, account, type) => {
  const resourcesForm = new FormData();
  for (const item of data) {
    if (item.image && item.image instanceof File) {
      resourcesForm.append('file', item.image, item.image.name, item.image.type);
    }
  }

  let resourcesCId;
  if (resourcesForm.has('file')) {
    resourcesForm.append('metadata', new File([ JSON.stringify({ name: name, owner: account, type: `${ type }Images` }) ], 'metadata.json', { type: 'application/json' }));
    const resourcesCIds = await uploadIpfsFolder(resourcesForm, account);
    resourcesCId = resourcesCIds[resourcesCIds.length - 1].Hash;
  }

  const jsonsForm = new FormData();
  for (const idx in data) {
    if (data[idx].image && data[idx].image instanceof File) {
      data[idx].image = `ipfs://${ resourcesCId }/${ data[idx].image.name }`
    }
    jsonsForm.append('file', new File([ JSON.stringify(data[idx]) ], idx, { type: 'application/json' }));
  }
  jsonsForm.append('metadata', new File([ JSON.stringify({ name: name, owner: account, type: `${ type }JSONs` }) ], 'metadata.json', { type: 'application/json' }));

  const jsonsCIds = await uploadIpfsFolder(jsonsForm, account);
  return jsonsCIds[jsonsCIds.length - 1].Hash;
}

const getCollectionResources = async (uri, account) => {
  const { cId: jsonCId } = ipfsParts(uri);
  const jsonContent = await getIpfsFolder(jsonCId, account);
  const jsons = {};
  let resourcesUri;
  for (const content of jsonContent) {
    const json = await ipfsFetch(content.Hash);
    if (!resourcesUri) resourcesUri = json.image;
    jsons[content.Name] = json;
    // jsons[content.Name] = new File([json], content.Name, { type: 'application/json' });
  }

  const { cId: resourcesCId } = ipfsParts(resourcesUri);
  const resourcesContent = await getIpfsFolder(resourcesCId, account);
  const resources = {};
  for (const content of resourcesContent) {
    const resource = await ipfsFetch(content.Hash);
    resources[content.Name] = new File([resource], content.Name, { type: resource instanceof Blob ? resource.type : 'application/json' });
  }
  return { jsons, resources }
}

const updateCollection = async (data, jsons, resources, account) => {
  const resourcesForm = new FormData();
  for (const file of Object.values(resources)) {
    resourcesForm.append('file', file, file.name, file.type);
  }
  for (const json of data) {
    resourcesForm.append('file', json.image, json.image.name, json.image.type);
  }
  const resourcesCIds = await uploadIpfsFolder(resourcesForm, account);
  const resourcesCId = resourcesCIds[resourcesCIds.length - 1].Hash;

  const jsonsForm = new FormData();
  for (const [name, json] of Object.entries(jsons)) {
    if (json.image) {
      const uri = json.image.split('/');
      uri[2] = resourcesCId;
      json.image = uri.join('/');
    }
    const file = new File([JSON.stringify(json)], name, { type: 'application/json' });
    jsonsForm.append('file', file, name, 'application/json');
  }
  let nextId = Math.max(...(Object.keys(jsons).filter(key => !isNaN(key)).map(k => parseInt(k)))) + 1;
  for (const json of data) {
    json.image = `ipfs://${ resourcesCId }/${ json.image.name }`;
    const file = new File([JSON.stringify(json)], nextId, { type: 'application/json' });
    jsonsForm.append('file', file, nextId, 'application/json');
    nextId += 1;
  }
  const jsonsCIds = await uploadIpfsFolder(jsonsForm, account);
  return jsonsCIds[jsonsCIds.length - 1].Hash;
}

export const setupCollection = async (chain, data, oracle) => {
  let account;
  walletAddress.subscribe(wallet => account = wallet)();
  const cId = await setupResources(data.name, data.tokens, account, 'Collection');

  const maxSupply = [];
  const amounts = [];
  const decimals = [];
  for (const token of data.tokens) {
    if (token.symbol) {
      const amount = parseEther(token.amount.toString());
      maxSupply.push(amount);
      amounts.push(amount);
      decimals.push(18);
    } else {
      maxSupply.push(token.amount);
      amounts.push(0);
      decimals.push(0);
    }
  }

  const [operator, vault, fee] = await Promise.all([
    getContractValue(chain, oracle, 'getKey', [ data.operator || 'defaultOperator' ]),
    getContractValue(chain, oracle, 'getKey', [ data.vault || 'defaultVault' ]),
    getContractValue(chain, oracle, 'getKey', [ data.fee || 'collectionDeployFee' ])
  ]);
  return {
    data: [
      data.name, `ipfs://${ cId }/{id}`, maxSupply, amounts, decimals, data.amIPrivate,
      operator, vault, parseEther(fee)
    ],
    fee: parseEther(fee)
  }
}

export const setupProducts = async (chain, data, oracle) => {
  let account;
  walletAddress.subscribe(wallet => account = wallet)();
  const cId = await setupResources(data.name, data.tokens, account, 'Products');

  const supplies = [];
  const fungibles = [];
  for (const token of data.tokens) {
    if (token.symbol) {
      supplies.push(parseEther(token.amount.toString()));
      fungibles.push(true);
    } else {
      supplies.push(token.amount);
      fungibles.push(false);
    }
  }

  const [operator, vault, fee] = await Promise.all([
    getContractValue(chain, oracle, 'getKey', [ data.operator || 'defaultOperator' ]),
    getContractValue(chain, oracle, 'getKey', [ data.vault || 'defaultVault' ]),
    getContractValue(chain, oracle, 'getKey', [ data.fee || 'productsDeployFee' ])
  ]);
  return {
    data: [
      data.name, `ipfs://${ cId }/{id}`, supplies, fungibles, data.amIPrivate, operator, vault, parseEther(fee)
    ],
    fee: parseEther(fee)
  }
}

export const setupVending = async (chain, data, oracle) => {
  const holders = [];
  const shares = [];
  for (const st of data.stakeholders) {
    holders.push(st.wallet);
    shares.push(st.shares);
  }
  const [operator, vault, fee] = await Promise.all([
    getContractValue(chain, oracle, 'getKey', [ data.operator || 'defaultOperator' ]),
    getContractValue(chain, oracle, 'getKey', [ data.vault || 'defaultVault' ]),
    getContractValue(chain, oracle, 'getKey', [ data.fee || 'vendingDeployFee' ])
  ]);
  return {
    data: [
      data.name,
      data.contractAddress, data.tokenId, data.holder || zeroAddress,
      parseEther(data.price.toString()), data.amount,
      holders, shares,
      vault, parseEther(fee), operator
    ],
    fee: parseEther(fee)
  }
}

export const setupFaucet = async (chain, data, oracle) => {
  const minutes = 60 * 1000;
  const Hours = 60 * minutes;
  const Days = 24 * Hours;
  const Months = 43800 * minutes;
  const recurrences = { Hours, Days, Weeks: 7 * Days, Months, Years: 12 * Months };
  const triggerTypes = [ 'Auto', 'Image', 'Youtube', 'Button', 'Iframe' ];
  const frequencies = [ 'As they come', 'Hourly', 'Daily', 'Weekly', 'Monthly', 'Yearly' ];

  const [operator, vault, fee, fireContract, fireFee] = await Promise.all([
    getContractValue(chain, oracle, 'getKey', [ data.operator || 'defaultOperator' ]),
    getContractValue(chain, oracle, 'getKey', [ data.vault || 'defaultVault' ]),
    getContractValue(chain, oracle, 'getKey', [ data.fee || 'faucetDeployFee' ]),
    getContractValue(chain, oracle, 'getKey', [ data.fee || 'defaultFireContract' ]),
    getContractValue(chain, oracle, 'getKey', [ data.fee || 'faucetFireFee' ])
  ]);
  return {
    data: [
      data.name,
      {
        contractAddress: data.contractAddress,
        tokenId: data.holder ? parseInt(data.tokenId) : data.tokenId,
        holder: data.holder || '0x0000000000000000000000000000000000000000'
      },
      { amount: data.amount, maxPerUser: data.maxAmount, totalMax: data.totalMax },
      {
        recurrence: recurrences[data.recurrence.unit] * data.recurrence.amount,
        start: Date.parse(data.active.start) / 1000, finish: Date.parse(data.active.end) / 1000,
        allTokens: data.neededTokens?.oneOf || false
      },
      [ ...(data.neededTokens?.tokens || []).map(t => ({ tokenId: t, amount: 1 })) ],
      { ttype: triggerTypes.indexOf(data.trigger.type), data: data.trigger.data },
      frequencies.indexOf(data.frequency),
      { fireAddress: fireContract, fee: parseEther(fireFee) },
      operator, vault, parseEther(fee)
    ],
    fee: parseEther(fee)
  }
}

export const setupCoin = async (chain, data, oracle) => {
  const json = { name: data.name, symbol: data.symbol, decimals: 18, image: data.image }
  if (data.description) {
    json.description = data.description;
  }
  let account;
  walletAddress.subscribe(wallet => account = wallet)();
  const cId = await setupResources(data.name, [ json ], account, 'Coin');
  const [operator, vault, fee] = await Promise.all([
    getContractValue(chain, oracle, 'getKey', [ data.operator || 'defaultOperator' ]),
    getContractValue(chain, oracle, 'getKey', [ data.vault || 'defaultVault' ]),
    getContractValue(chain, oracle, 'getKey', [ data.fee || 'coinDeployFee' ])
  ]);
  return {
    data: [
      data.name, data.symbol, `ipfs://${ cId }/0`, data.amount, data.amIPrivate,
      operator, vault, parseEther(fee)
    ],
    fee: parseEther(fee)
  }
}

export const setupDynamicVault = async (chain, data, oracle) => {
  console.log(data)
  let [vault, fee] = await Promise.all([
    getContractValue(chain, oracle, 'getKey', [ 'defaultVault' ]),
    getContractValue(chain, oracle, 'getKey', [ 'dynamicVaultDeployFee' ])
  ]);
  fee = parseEther(fee);
  return {
    data: [
      data.name, data.symbol,
      data.maxSupply, parseEther(data.price.toString()), data.minimumShares,
      data.privateOnly,
      vault, fee
    ],
    fee: fee
  }
}

export const setupCollectionsFungible = async (chain, contract, data) => {
  let account;
  walletAddress.subscribe(wallet => account = wallet)();
  const uri = await getContractValue(chain, contract, 'uri', [0]);
  const { jsons, resources } = await getCollectionResources(uri, account)
  const cId = await updateCollection([ data ], jsons, resources, account);
  const amount = parseEther(data.amount.toString());
  return { data: [ `ipfs://${ cId }/{id}`, [ amount ], [ amount ], [ 18 ], '' ] }
}

export const setupCollectionsNonFungible = async (chain, contract, data) => {
  let account;
  walletAddress.subscribe(wallet => account = wallet)();
  const uri = await getContractValue(chain, contract, 'uri', [0]);
  const { jsons, resources } = await getCollectionResources(uri, account)
  const cId = await updateCollection([ data ], jsons, resources, account);
  return { data: [ `ipfs://${ cId }/{id}`, [ parseEther(data.amount.toString()) ], [ 0 ], [ 0 ], '' ] }
}

export const setupTransfer = async (chain, contract, data) => ({
  data: [ data.to, parseEther(data.amount.toString()) ]
});

export const setupTransferToken = async (chain, contract, data) => {
  const amount = await getContractJSONvalue(chain, contract, data.tokenId, 'symbol') ? parseEther(data.amount.toString()): data.amount;
  return { data: [ data.from, data.to, data.tokenId, amount, toHex(data.data) ] }
}

export const setupEnvironment = async (chain, data) => {
  const keys = [];
  const values = [];
  for (const pair of data.keys) {
    keys.push(pair.key)
    values.push(pair.value.toString());
  }
  const coupons = data.coupons.map(c => {
    if (!c.holder) c.holder = '0x0000000000000000000000000000000000000000';
    if (!c.tokenId) c.tokenId = 0;
    return c.name
  });

  return { data: [ data.name, keys, values, coupons, data.coupons, data.vault, data.fee ], fee: data.fee }
}

export const setupNewEnvironmentPair = async (chain, contract, data) => ({ data: [ data.key, data.value ] });

export const setupCoupon = async (chain, contract, data) => {
  if (!data.holder) data.holder = zeroAddress;
  if (!data.tokenId) data.tokenId = 0;
  return { data: [ data.name, data ] }
}
